// TODO: Task 904718: refactor to use explicit dependency for es6 syntax rather than global variable
/* global Map */
function getMapKeys(map) {
  const keys = [];

  map.forEach((value, key) => keys.push(key));
  return keys;
}

function getMapValues(map) {
  const values = [];

  map.forEach(value => values.push(value));
  return values;
}

function getParentAndRef(tree, item) {
  const { parent, ref } = tree.nodes.get(item);
  return { parent, ref };
}

function getChildren(tree, item) {
  return tree.nodes.get(item).children;
}

function getChild(tree, item, ref) {
  return getChildren(tree, item).get(ref);
}

function createNode(tree, item, parent, ref) {
  tree.nodes.set(item, {
    parent,
    ref,
    children: new Map(),
  });

  getChildren(tree, parent).set(ref, item);
}

function removeNode(tree, item) {
  const { parent, ref } = tree.nodes.get(item);

  removeNodeChildren(tree, item); // eslint-disable-line no-use-before-define
  getChildren(tree, parent).delete(ref);
  tree.nodes.delete(item);
}

function removeNodeChildren(tree, item) {
  getChildren(tree, item).forEach(child => removeNode(tree, child));
}

function moveChild(tree, parentOld, refOld, parentNew, refNew) {
  const childrenOld = getChildren(tree, parentOld);
  const childrenNew = getChildren(tree, parentNew);
  const item = childrenOld.get(refOld);
  const node = tree.nodes.get(item);

  childrenOld.delete(refOld);
  childrenNew.set(refNew, item);
  node.parent = parentNew;
  node.ref = refNew;
}

function getPathOfItem(tree, item) {
  const refs = [];
  let itemIter = item;

  while (itemIter) {
    const { parent, ref } = getParentAndRef(tree, itemIter);

    refs.push(ref);
    itemIter = parent;
  }

  refs.reverse();

  return `/${refs.join('/')}`;
}

function getItemByPath(tree, path) {
  const refs = path.split('/');
  let item = null;

  for (let index = 0; index < refs.length; index += 1) {
    const ref = refs[index];

    if (ref) {
      item = getChild(tree, item, ref);

      if (!item) {
        break;
      }
    }
  }

  return item;
}

/**
 * A nonintrusive tree model.
 * You may use this class to setup a hierarchical structure among a set of items
 * without injecting properties into them.
 * With this tree model, each edge has a name called 'ref'. For a given node,
 * the 'ref's to each child MUST be unique.
 * NOTE: The root element of the tree is always `null`.
 */
export class ShadowTree {
  constructor() {
    this.nodes = new Map();
    this.nodes.set(null, {
      parent: null,
      children: new Map(),
      ref: null,
    });
  }

  /**
   * Test if an item exists in the tree
   * @param {Object} item - The item to test
   * @returns {boolean} - True if the item exists in the tree
   */
  hasNode(item) {
    return this.nodes.has(item);
  }

  /**
   * Get the parent item and ref for the given item
   * @param {Object} item - The issued item
   * @returns {{parent: Object, ref: string}} - The parent item and the ref
   */
  getParentAndRef(item) {
    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getParentAndRef(this, item);
  }

  /**
   * Get refs for all children of the given item
   * @param {Object} item - The issued item
   * @returns {string[]} - An array of all refs
   */
  getChildrenRefs(item) {
    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getMapKeys(getChildren(this, item));
  }

  /**
   * Get all children of the given item
   * @param {Object} item - The issued item
   * @returns {Object[]} - An array of all children
   */
  getChildren(item) {
    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getMapValues(getChildren(this, item));
  }

  /**
   * Get the child of the given item with the specified ref
   * @param {Object} item - The issued item
   * @param {string} ref - The ref for the child
   * @returns {Object} - The child with the given ref. Returns `undefined` if
   * there's no child with the given ref
   */
  getChild(item, ref) {
    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getChild(this, item, ref);
  }

  /**
   * Add a new node in the tree with the given item
   * @param {Object} item - The item of the new node
   * @param {Object} parent - The item of the parent node
   * @param {string} ref - The ref to specified the new node from the parent
   * @returns {void}
   */
  createNode(item, parent, ref) {
    if (typeof item === 'undefined') {
      throw new Error('The item cannot be undefined');
    }

    if (this.hasNode(item)) {
      throw new Error('Duplicated node creation');
    }

    if (!this.hasNode(parent)) {
      throw new Error('The parent item is not in the tree');
    }

    if (getChild(this, parent, ref)) {
      throw new Error('Cannot override existing child');
    }

    return createNode(this, item, parent, ref);
  }

  /**
   * Remove all descendants of a given item
   * @param {Object} item - The issued item
   * @returns {void}
   */
  removeNodeChildren(item) {
    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return removeNodeChildren(this, item);
  }

  /**
   * Remove an item from the tree
   * @param {Object} item - The item to remove
   * @returns {void}
   */
  removeNode(item) {
    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    if (item === null) {
      throw new Error('Cannot remove the virtual root node');
    }

    return removeNode(this, item);
  }

  /**
   * Move an item to a new place in the tree
   * @param {Object} parentOld - The old parent of the item
   * @param {string} refOld - The old ref of the item
   * @param {Object} parentNew - The new parent of the item
   * @param {string} refNew - The new ref of the item
   * @returns {void}
   */
  moveChild(parentOld, refOld, parentNew, refNew) {
    if (parentNew === parentOld && refOld === refNew) {
      return;
    }

    if (!this.hasNode(parentOld)) {
      throw new Error('The old parent is not in the tree');
    }

    if (!this.hasNode(parentNew)) {
      throw new Error('The new parent is not in the tree');
    }

    if (!getChild(this, parentOld, refOld)) {
      throw new Error('The child to move does not exist');
    }

    if (getChild(this, parentNew, refNew)) {
      throw new Error('Cannot override existing child');
    }

    moveChild(this, parentOld, refOld, parentNew, refNew);
  }

  /**
   * Get a '/' separated full path of a given item, which identifies an unique
   * node in the tree
   * @param {Object} item - The issued item
   * @returns {string} - The full path to direct to the item from root
   */
  getPathOfItem(item) {
    if (!this.hasNode(item)) {
      throw new Error('The item is not in the tree');
    }

    return getPathOfItem(this, item);
  }

  /**
   * Query an item from the tree with its path
   * @param {string} path - The '/' separated path
   * @returns {Object} - The item at the path or `undefined` if not found
   */
  getItemByPath(path) {
    if (!path[0] === '/') {
      throw new Error('Expect an absolute path');
    }

    return getItemByPath(this, path);
  }
}
